import React, { useEffect, useMemo, useState } from 'react'
import { Box, Container, Typography, withStyles } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import { componentBoxHideAction } from 'src/reducers/component-box/action'
import UserType from 'src/model/user-types'
import PLANS from './data'
import { Header, PricingCard, TableInvoice, TrustedBy, CustomerQuote } from './components'
import style from './style'
import { roleTypes, AUDIENCES, AUDIENCE_BY_GROUP } from './data/pricing-json'
import {
  handlePortalRedirection,
  handleStripeCheckout,
  trackPlanUpgrade,
  getAction,
  getUpgradeAction,
  getCheckoutMode,
} from './logic'
import { meService } from 'src/api/authentication'


const Pricing = props => {
  const { classes } = props
  const dispatch = useDispatch()
  const [timeInterval, setTimeInterval] = useState(true)
  const [audience, setAudience] = useState(AUDIENCES.CREATOR)
  const [type, setType] = useState('')
  const [loading, setLoading] = useState(false)
  const [groupId, setGroupId] = useState(-1)
  const [trialExpired, setTrialExpired] = useState(false)

  const getGroupIdById = id => {
    const role = roleTypes.find(role => role.id === id)
    return role ? role.groupId : undefined
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    dispatch(componentBoxHideAction())

    meService().then(response => {
      const userType = response.data.userRoleId

      setType(UserType[userType])
      setTrialExpired(response.data.trialExpired || false)

      const groupId = getGroupIdById(userType)
      setGroupId(groupId)
      // Open on the tab the user's current plan belongs to.
      setAudience(AUDIENCE_BY_GROUP[groupId] ?? AUDIENCES.CREATOR)
    })
  }, [])

  const visiblePlans = useMemo(
    () =>
      PLANS({ timeInterval, groupId, getAction, getUpgradeAction }).filter(
        plan => plan.audience === audience,
      ),
    [timeInterval, groupId, audience],
  )

  const handleClick = async card => {
    const { priceId, priceTrialDays, successReturnUrl, subscription } = card

    // Plans without a Stripe price (Plus / Pro v2 / Launch) aren't purchasable
    // yet. Their buttons are already disabled; bail before we track or redirect.
    if (!priceId) return

    setLoading(true)
    trackPlanUpgrade(subscription)

    try {
      const mode = getCheckoutMode(groupId)
      if (mode === 'checkout') {
        await handleStripeCheckout(priceId, priceTrialDays, successReturnUrl)
      } else if (mode === 'portal') {
        await handlePortalRedirection()
      }
    } catch (error) {
      console.error('Plan upgrade error:', error)
    } finally {
      setLoading(false)
    }
  }

  const isBasicPlan =
    UserType.isTrialEquivalent(type) ||
    type === 'Basic' ||
    type?.includes('AppSumo')
  const badgeBg = isBasicPlan ? '#E5E7EB' : '#EFFCEF'
  const badgeColor = isBasicPlan ? '#002E47' : '#02791A'

  return (
    <Container maxWidth="xl" className={classes.root}>
      {type && !trialExpired && (
        <Box
          className={classes.currentPlanBadge}
          style={{ backgroundColor: badgeBg, color: badgeColor }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <circle cx="5" cy="5" r="5" fill={badgeColor} />
          </svg>
          Current Plan: {UserType.getDisplayRoleName(type)}
        </Box>
      )}
      <Header />
      <PricingCard
        plans={visiblePlans}
        audience={audience}
        loading={loading}
        handleClick={handleClick}
        renderToggle={() => (
          <>
            <Box className={classes.audienceToggle}>
              <Box
                className={`${classes.audienceOption} ${audience === AUDIENCES.CREATOR ? classes.audienceActive : ''}`}
                onClick={() => setAudience(AUDIENCES.CREATOR)}
              >
                Creator
              </Box>
              <Box
                className={`${classes.audienceOption} ${audience === AUDIENCES.ENTERPRISE ? classes.audienceActive : ''}`}
                onClick={() => setAudience(AUDIENCES.ENTERPRISE)}
              >
                Enterprise
              </Box>
            </Box>
            <Box className={classes.billingToggle}>
              <span
                className={`${classes.billingLabel} ${!timeInterval ? classes.billingLabelActive : ''}`}
                onClick={() => setTimeInterval(false)}
              >
                Monthly
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={timeInterval}
                aria-label="Toggle annual billing"
                className={`${classes.billingSwitch} ${timeInterval ? classes.billingSwitchOn : ''}`}
                onClick={() => setTimeInterval(!timeInterval)}
              >
                <span className={`${classes.billingKnob} ${timeInterval ? classes.billingKnobOn : ''}`}>
                  {timeInterval && (
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#015FFB"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
              </button>
              <span
                className={`${classes.billingLabel} ${timeInterval ? classes.billingLabelActive : ''}`}
                onClick={() => setTimeInterval(true)}
              >
                Yearly
              </span>
              <span className={`${classes.billingBadge} ${timeInterval ? classes.billingBadgeOn : ''}`}>
                Save up to 20%
              </span>
            </Box>
          </>
        )}
      />
      <TrustedBy />
      {audience === AUDIENCES.ENTERPRISE && <CustomerQuote />}
      <div className={classes.rootHeader}>
        <Typography align="center" className={classes.typographyHeader}>
          Compare All Features
        </Typography>
      </div>
      <TableInvoice
        plans={visiblePlans}
        loading={loading}
        handleClick={handleClick}
      />
    </Container>
  )
}

export default withStyles(style)(Pricing)
